import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Shield } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { localized } from "@/i18n";
import { CATEGORIES, FILTER_TAGS, priceSymbol } from "@/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import AdminCollections from "@/pages/admin/AdminCollections";
import { ExperienceFeed } from "@/components/experiences/ExperienceFeed";
import AdminServices from "@/pages/admin/AdminServices";

const EMPTY = {
  name: "", category: "manger", tags: [], price_level: 2, neighborhood: "",
  lat: 39.4699, lng: -0.3763, images: [],
  summary: { fr: "", en: "", es: "" }, why_go: { fr: "", en: "", es: "" },
  hours: {}, website: "", phone: "", booking_url: "", address: "", duration_min: 60,
  best_moments: [], team_pick: false, good_first_visit: false, popular_students: false, ideal_evening: false, status: "open",
};

function Field({ label, children }) {
  return <label className="block"><span className="mb-1 block text-xs font-semibold text-ink">{label}</span>{children}</label>;
}
const inputCls = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-terracotta";

export default function Admin() {
  const navigate = useNavigate();
  const { t, lang, user, authChecked } = useApp();
  const { toast } = useToast();
  const [places, setPlaces] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("places");

  const load = () => api.get("/places", { params: { limit: 300 } }).then((r) => setPlaces(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!authChecked) return <p data-testid="admin-auth-loading" className="py-12 text-center">{t("loading")}</p>;
  if (!user || !user.is_admin) {
    return (
      <div className="flex flex-col items-center gap-3 py-20 text-center">
        <Shield className="h-10 w-10 text-ink-muted" />
        <p className="font-heading font-bold text-ink">{t("admin_denied")}</p>
        <button onClick={() => navigate("/profile")} className="rounded-full bg-terracotta px-4 py-2 text-sm font-bold text-white">{t("back")}</button>
      </div>
    );
  }

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      price_level: Number(form.price_level),
      lat: Number(form.lat), lng: Number(form.lng),
      duration_min: form.duration_min ? Number(form.duration_min) : null,
    };
    try {
      if (editing) await api.put(`/admin/places/${editing}`, payload);
      else await api.post("/admin/places", payload);
      toast({ title: t("saved") });
      setOpen(false); load();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/places/${id}`); load(); } catch {}
  };

  const toggleTag = (tag) => setForm((f) => ({ ...f, tags: f.tags.includes(tag) ? f.tags.filter((x) => x !== tag) : [...f.tags, tag] }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/profile")} className="grid h-9 w-9 place-items-center rounded-full bg-sand text-ink"><ArrowLeft className="h-4.5 w-4.5" /></button>
          <h1 className="font-heading text-xl font-extrabold text-ink">{t("admin")}</h1>
        </div>
        {tab === "places" && <button data-testid="admin-add-place" onClick={openNew} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-3.5 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> {t("add_place")}</button>}
      </div>

      <div className="grid grid-cols-2 gap-1 rounded-xl bg-sand p-1 sm:grid-cols-4" data-testid="admin-tabs">
        {[["places", t("admin_tab_places")], ["collections", t("admin_collections")], ["experiences", t("admin_experiences")], ["services", t("ai_services")]].map(([id, label]) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)} className={`rounded-full px-2 py-2 text-sm font-bold transition-colors ${tab === id ? "bg-white text-terracotta shadow-sm" : "text-ink-muted"}`}>{label}</button>
        ))}
      </div>

      {tab === "services" ? <AdminServices /> : tab === "experiences" ? <ExperienceFeed admin /> : tab === "collections" ? <AdminCollections places={places} /> : (<>
      <p className="text-sm text-ink-muted">{places.length} {t("all_places").toLowerCase()}</p>

      <div className="space-y-2">
        {places.map((p) => {
          const cat = CATEGORIES.find((c) => c.id === p.category);
          return (
            <div key={p.id} data-testid={`admin-row-${p.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-white p-3">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-sand">{p.images?.[0] && <img src={p.images[0]} alt="" className="h-full w-full object-cover" />}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-ink">{p.name}</p>
                <p className="text-xs text-ink-muted">{localized(cat?.label, lang)} · {p.neighborhood} · {priceSymbol(p.price_level)}</p>
              </div>
              <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="grid h-9 w-9 place-items-center rounded-full text-sea hover:bg-sea-light"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p.id)} className="grid h-9 w-9 place-items-center rounded-full text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto rounded-2xl" data-testid="admin-form">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("edit") : t("add_place")}</DialogTitle><DialogDescription className="sr-only">{t("admin_places")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Field label="Nom"><input data-testid="admin-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Catégorie"><select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label.fr}</option>)}</select></Field>
              <Field label="Budget (1-3)"><select className={inputCls} value={form.price_level} onChange={(e) => setForm({ ...form, price_level: e.target.value })}>{[1, 2, 3].map((n) => <option key={n} value={n}>{priceSymbol(n)}</option>)}</select></Field>
            </div>
            <Field label="Quartier"><input className={inputCls} value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude"><input className={inputCls} value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} /></Field>
              <Field label="Longitude"><input className={inputCls} value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} /></Field>
            </div>
            <Field label="Images (URLs séparées par des virgules)"><textarea className={inputCls} rows={2} value={form.images.join(", ")} onChange={(e) => setForm({ ...form, images: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
            <Field label="Résumé (FR)"><textarea className={inputCls} rows={2} value={form.summary.fr} onChange={(e) => setForm({ ...form, summary: { ...form.summary, fr: e.target.value } })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Summary (EN)"><textarea className={inputCls} rows={2} value={form.summary.en} onChange={(e) => setForm({ ...form, summary: { ...form.summary, en: e.target.value } })} /></Field>
              <Field label="Resumen (ES)"><textarea className={inputCls} rows={2} value={form.summary.es} onChange={(e) => setForm({ ...form, summary: { ...form.summary, es: e.target.value } })} /></Field>
            </div>
            <Field label="Pourquoi y aller (FR)"><textarea className={inputCls} rows={2} value={form.why_go.fr} onChange={(e) => setForm({ ...form, why_go: { ...form.why_go, fr: e.target.value } })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Site web"><input className={inputCls} value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
              <Field label="Durée (min)"><input className={inputCls} value={form.duration_min || ""} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></Field>
            </div>
            <Field label="Statut"><select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="open">Ouvert</option><option value="temporarily_closed">Fermé temporairement</option></select></Field>
            <div>
              <span className="mb-1 block text-xs font-semibold text-ink">Tags</span>
              <div className="flex flex-wrap gap-1.5">
                {FILTER_TAGS.map((tag) => <button key={tag} type="button" onClick={() => toggleTag(tag)} className={`rounded-full border px-2.5 py-1 text-xs font-medium ${form.tags.includes(tag) ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-ink"}`}>{tag}</button>)}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {["team_pick", "good_first_visit", "popular_students", "ideal_evening"].map((k) => (
                <label key={k} className="inline-flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} /> {k}</label>
              ))}
            </div>
            <button data-testid="admin-save-place" disabled={saving} onClick={save} className="w-full rounded-xl bg-terracotta py-3 text-sm font-bold text-white disabled:opacity-60">{t("save_place")}</button>
          </div>
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
}
